import { Component, Inject, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { L10N_LOCALE, L10nLocale, L10nTranslatePipe } from 'angular-l10n';
import { AppDatabaseService, DbResultSet } from '../../app-database/app-database.service';
import { AnalyticsHelper } from '../../analytics/analytics-helper';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmComponent } from '../../modals/confirm/confirm.component';
import { AUTH_EVENT_KEY, AuthService } from '../auth.service';
import { OidcUser } from '../providers/oidc.provider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { FixDatePipe, SafeDatePipe } from '../../pipes/fix-date/fix-date.pipe';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';

export interface AuthEventItem {
  name: string,
  evtTime: Date,
  message?: string,
  userId?: string,
  authTime?: Date,
  expTime?: Date,
  tokenType?: string,
  idpType?: string,
  user?: OidcUser,
  payload?: object | string,
}

@Component({
  selector: 'app-auth-events',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './auth-events.component.html',
  styleUrls: ['./auth-events.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SafeDatePipe,
    MatTableModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    L10nTranslatePipe,
    FixDatePipe,
  ],
})
export class AuthEventsComponent implements OnInit {
  results: AuthEventItem[] = null;
  columns: string[] = ['name', 'evtTime', 'userId', 'authTime', 'expTime', /*'tokenType',*/ 'idpType', 'message'];

  constructor(
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly database: AppDatabaseService,
    private readonly authService: AuthService,
    private readonly dialog: MatDialog,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.fetchResults();
  }

  onClear() {
    const data = { data: { title: 'Clear Events', message: 'Are you sure you want to clear all events?' } };
    this.dialog.open(ConfirmComponent, data).afterClosed().subscribe(result => {
      if (result) {
        this.results = undefined;
        this.database.clearEvents(AUTH_EVENT_KEY).subscribe(count => {
          const event = { action: AUTH_EVENT_KEY, name: 'LogCleared', message: `${count} events cleared`, detail: this.authService.provider.getCurrentUser() as OidcUser };
          this.database.storeEvent(event).subscribe(_ => {
            this.fetchResults();
          });
        });
      }
    });
  }

  private fetchResults() {
    this.database.getEvents(AUTH_EVENT_KEY).subscribe(results => {
      this.processResults(results);
      this.cdr.markForCheck();
    });
  }

  private processResults(results: DbResultSet) {
    this.results = results?.items?.map(el => {
      const user = el.detail as OidcUser;
      const item: AuthEventItem = {
        name: el.name,
        evtTime: new Date(el.time),
        message: el.message ?? 'none',
        payload: el.detail,
      };
      if (user?.profile) {
        item.user = user;
        if (user?.profile?.sub) item.userId = user.profile.sub;
        if (user?.token_type) item.tokenType = user.token_type;
        if (user?.profile?.idp) item.idpType = user.profile.idp as string;
        if (user?.profile?.auth_time) item.authTime = new Date(user.profile.auth_time * 1000);
        if (user?.expires_at) item.expTime = new Date(user.expires_at * 1000);
      } else {
        item.message = AnalyticsHelper.convertPayload(el.detail, 128, '; ') ?? 'none';
      }

      return item;
    });
  }
}
